import React, { useEffect, useRef } from 'react';
import { useContext } from 'react';
import { Context } from "routes/private/Editor/Context";
import { IProps } from './types';
import { IState} from '@routes/private/Editor/Editor/CommandsBars/MainCommandBar/CommandsOptions/PositionCommandOption/types';
import * as S from './styles';
import TextIcon from '@assets/icons/textIcon';
import ImageIcon from '@assets/icons/imageIcon';
import ShapeIcon from '@assets/icons/shapeIcon';
import NotHiddenIcon from '@assets/icons/notHiddenIcon';
import HiddenIcon from '@assets/icons/hiddenIcon';
import CheckIcon from '@assets/icons/checkIcon';
import { LockedIcon } from '@assets/icons/lockedIcon';
import { UnlockedIcon } from '@assets/icons/unlockedIcon';
import ClickOutside from '@components/ClickOutside';
import { findArtboardIndex } from '@utils/editor';
import { cloneDeep } from 'lodash';
import { Tittle } from '@routes/private/Templates/components/ListTemplate/components/List/styles';
import { IArtboardZIndexChain } from '@routes/private/Editor/types';

const LayerElement:React.FC<IProps> = (props) => {
  const [dragEnterTarget, setDragEnterTarget] = React.useState();
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [isDropping, setIsDropping] = React.useState<boolean>(false);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);  
  const [title, setTitle] = React.useState<string>();
  const editInput = useRef<HTMLInputElement>(null);
  const context = useContext(Context);  
  const handleDragStart = () => {
    if (props.onClick) props.onClick();
    setIsDragging(true);
  }

  const handleDragEnd = () => {
    setIsDragging(false);
  }
  
  const handleDragOver = (e: any) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    return false;
  }

  const handleDragEnter = (e: any) => {
    setDragEnterTarget(e.target);
    setIsDropping(true);
  }

  const handleDragLeave = (e: any) => {
    if (dragEnterTarget === e.target) {
      setIsDropping(false);
    }
  }

  const updateChain = (newChains: IState['chains']) => {
    context.onCommandChange('changePositionsChain', newChains);
  }

  const insertElementIfExists = (newChains: IArtboardZIndexChain[], newChainsIndex: number, artboardIndex: number) => {
    context.editorStatus.selectedElementsId.forEach(selectedElementId => {
      let artboardIndexSelectedElement = findArtboardIndex(context.data[0]?.children ?? [], selectedElementId);
      if (artboardIndex === artboardIndexSelectedElement) {
        newChains[newChainsIndex].zIndexChain.push(selectedElementId);
      }
    });
  }

  const changePosition = () => {
    let newChains: IState['chains'] = cloneDeep(context.artboardsZIndexChain);
    if (context.artboardsZIndexChain && context.artboardsZIndexChain.length>0) {
        const artboardIndex = props.artboardIndex; 
        if (artboardIndex!==undefined && artboardIndex>=0) {
          const index = newChains.findIndex(chain => (chain.artboardIndex === artboardIndex));
          if (index >= 0) {
            newChains[index].zIndexChain = [];
            for (let i=0;i<context.artboardsZIndexChain[index].zIndexChain.length;i++) {
              if (props.id === context.artboardsZIndexChain[index].zIndexChain[i]) {
                insertElementIfExists(newChains, index, artboardIndex);
              }
              const existsInSelected = context.editorStatus.selectedElementsId.includes(context.artboardsZIndexChain[index].zIndexChain[i]);
              if (!existsInSelected) {
                newChains[index].zIndexChain.push(context.artboardsZIndexChain[index].zIndexChain[i]);
              }
            }
            if (props.id === "shadow-first-element") {
              insertElementIfExists(newChains, index, artboardIndex);
            }
            updateChain(newChains);
          }
        }
    }
  }

  const handleDrop = (e: any) => {
    setIsDropping(false);
    changePosition();
  }

  const handleKeyDown=(e:any)=>{    
        
    if (e.key=="Escape")
    {
      setTitle(props.title)
      setIsEditing(false);
    }
    if (e.key=="Enter")
    {    
      if (!isReservedWord())  
      {
        if (props.onEdit && editInput.current)
        {        
          props.onEdit(editInput.current.value)               
        }
        setIsEditing(false);
      }
    }    

  }

  const isReservedWord = ()=>
  {
    let text = editInput.current!.value??''
    return text.toUpperCase()=="SET";          
  }

  const endEditing=()=>{    
    if (isEditing && props.onEdit && editInput.current)
    {    
      if (!isReservedWord())        
        props.onEdit(editInput.current.value)           
      else
        setTitle(props.title)
      setIsEditing(false);
    }
  }


  const handleClick=()=>{    
    if (props.selectDescribe??false)
    {
      setTimeout(()=>{    
        if (editInput.current ==null && props.onClick)
          props.onClick()
      },200)    
    }
    else if (props.onClick)
      props.onClick()
  }

  const handleDoubleClick=()=>{
    if (props.onDoubleClick)
      props.onDoubleClick()
    setIsEditing(true)
  }

  useEffect(()=>{
    setTitle(props.title)
  },[props.title])
  
  useEffect(()=>{
    if (isEditing)
      editInput.current?.focus();
  },[isEditing])


  return (
    <S.Root
      pickerType={props.pickerType}
      locked={props.locked??false}
      draggable={props.pickerType==="STACKS" && props.locked!==true ?true:false}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseOver={(e) => props.onOver(true)}
      onMouseOut={(e) => props.onOver(false)}
    >
      <S.GeneralContainer
        isShadow={props.id === "shadow-first-element"}
        isDropping={isDropping}
        pickerType={props.pickerType}
        level={props.level}
        selected={props.selected}
        isOver={props.isOver}
      >
        <S.CheckContainer onClick={handleClick} onDoubleClick={handleDoubleClick}>
        {props.parentColor && 
          <CheckIcon style={{fillColor: props.parentColor}}/>
        }
        </S.CheckContainer>
        {props.type==='Text' ? 
        <S.IconContainer onClick={handleClick} onDoubleClick={handleDoubleClick}>
          <TextIcon style={{overflow: 'inherit'}}/> 
        </S.IconContainer>
        :
        props.type==='Image' ? 
        <S.IconContainer onClick={handleClick} onDoubleClick={handleDoubleClick}>
          <ImageIcon style={{overflow: 'inherit'}}/> 
        </S.IconContainer>
        :
        props.type === 'Shape' ?
        <S.IconContainer onClick={handleClick} onDoubleClick={handleDoubleClick}>
          <ShapeIcon style={{overflow: 'inherit'}}/>
        </S.IconContainer>
        : <></>
        }
        <S.Title onClick={handleClick} 
        onDoubleClick={()=>setIsEditing(true)}                  
        >
          {
            isEditing?
            <ClickOutside onClickOutside={()=> endEditing()} event={"mousedown"}>
              <S.EditContainer value={title} onKeyDown={handleKeyDown} ref={editInput}
              onChange={event => setTitle(event.target.value)}>          
              </S.EditContainer>
            </ClickOutside>
              :
              title
            
          }
        </S.Title>
        {
          props.id !== "shadow-first-element" &&
          <>
            {props.locked ?
              <S.VisibilityIconContainer onClick={() => props.lockUnlock(props.id, false)}>
                <LockedIcon fill='#122442' width='9' height='12' style={{ overflow: 'inherit', marginLeft: 'auto', marginRight: '9px', cursor: 'pointer' }} />
              </S.VisibilityIconContainer>
              :
              <S.VisibilityIconContainer onClick={() => props.lockUnlock(props.id, true)}>
                <UnlockedIcon fill='#122442' width='13' height='12' style={{ overflow: 'inherit', marginLeft: 'auto', marginRight: '5px', cursor: 'pointer' }} />
              </S.VisibilityIconContainer>
            }
            {props.hidden ?
              <S.VisibilityIconContainer onClick={() => props.HideUnhide(props.id)}>
                <HiddenIcon style={{ overflow: 'inherit', marginLeft: 'auto', marginRight: '18px', cursor: 'pointer' }} />
              </S.VisibilityIconContainer>
              :
              <S.VisibilityIconContainer onClick={() => props.HideUnhide(props.id)}>
                <NotHiddenIcon style={{ overflow: 'inherit', marginLeft: 'auto', marginRight: '18px', cursor: 'pointer' }} />
              </S.VisibilityIconContainer>
            }
          </>
        }
      </S.GeneralContainer>      
    </S.Root>
  );
}

export default LayerElement;